"use strict";

import { card } from "../css_constants";

const $ = require("jquery");
const ldcEvents = require("js/event_helper.js");
const searchPopup = require("js/controls/search_popup.js");
const utilModule = require("js/util");
const viewRelations = require("js/editor/view_relations");
const viewBorders = require("js/editor/view_borders");
const viewUtils = require("js/editor/card_view_utils");
const keyModule = require("js/keyboard.js");
const alertModule = require("js/alert.js");
const confirmModule = require("js/confirm.js");
const cardModule = require("js/cards.js");
const dragDrop = require("js/utils/drag_drop.js");
const cardViewUtils = require("js/editor/card_view_utils.js");
const anchorLinkManager = require("js/editor/anchor_link_manager.js");

const keys = keyModule.keys;

const TAG_NAME = "ldc-editor-card-view";
const NAME = "card_view";
const TYPE = NAME;
const ATTR_PURPOSE = "data-purpose";

const STATE_REQUEST_NEW = "request_new";
const STATE_REQUEST_FROM_SAVED_CARD = "request_from_saved_card";
const STATE_REQUEST_FROM_COPY = "request_from_copy";

const PURPOSE_SHOW = "show";

const MIN_WIDTH = 100;
const MIN_HEIGHT = 100;

import { LDCAbstractEditor } from "./abstract_editor.js";
import { highlightIfInViewPort, waitForVisibleElement } from "../util";
import alerts from "../alert";

function pushNewScale(self, id) {
    return (e) => {
        const meta = {
            id: id,
            scale: e.target.value,
        };

        self.pushEvent("scale-object", meta);
    };
}

class CardView extends LDCAbstractEditor {
    constructor() {
        super(NAME);
        this.__type = null;
    }

    connectedCallback() {
        if (this.dataset.purpose == PURPOSE_SHOW) {
            this.__connectLiveView();
        }
    }

    disconnectedCallback() {
        // there is a function destroyViewByEl on the liveSocket, but for some reason that does
        // not actually destroy the view for some reason, but only its children...
        window.ldc.liveSocket.roots[this.firstElementChild.id].destroy();
        console.log(`${this.tagName}: disconnected from LiveView`);

        utilModule.removeAllChildren(this);
    }

    __connectLiveView() {
        // CAVE: this is what live_view.js does internally, might break on every update...
        // used to call a function joinRootView(container, href), see update to live_view 0.17 for history
        window.ldc.liveSocket.joinRootViews();

        console.log(`${this.tagName}: LiveView connected`);
    }

    __stateId() {
        const view = this.querySelector(".card-view-editor");
        return view == null ? null : view.getAttribute("data-state-id");
    }

    /***********************
     * ABSTRACT OVERWRITES *
     ***********************/

    cardTypeChanged(type) {
        this.__type = type;
        this.dispatchEvent(new CustomEvent("cardTypeChanged"));
    }

    __doGetContentForSaving(cardType) {
        const state = this.__mementoFromState();
        state["type"] = TYPE;
        return state;
    }

    __mementoFromState() {
        return {
            // Note: state_id _must_ be written as state_id, because we are converting the value to an
            // _existing_ atom on the server, and stateId does not exist, so you'll get an error on the
            // saving if you save the card then
            state_id: this.__stateId(),
        };
    }

    __mementoFromCardContent(card, content) {
        return {
            state_id: STATE_REQUEST_FROM_SAVED_CARD,
            order: content.order,
            cardId: card.id,
        };
    }

    __hasBeenModified(memento) {
        const cardViewEditor = this.querySelector(".card-view-editor");
        if (cardViewEditor) {
            return utilModule.parseBool(cardViewEditor.dataset.modified);
        } else {
            return false;
        }
    }

    __setState(memento) {
        let url;

        if (memento.state_id == STATE_REQUEST_NEW) {
            url = `/api/view/editor/new/${memento.cardId}/`;
        } else if (memento.state_id == STATE_REQUEST_FROM_SAVED_CARD) {
            url = `/api/view/editor/for/${memento.cardId}/${memento.order}`;
        } else if (memento.state_id == STATE_REQUEST_FROM_COPY) {
            url = `/api/view/editor/copy/${memento.cardId}`;
        } else {
            console.error(
                "reloading of state from browser history has not yet been implemented, falling back to creating a new view",
            );
            url = "/api/view/editor/new";
        }

        const self = this;
        return $.ajax({
            url: url,
            method: "GET",
            cache: false,
            headers: {
                "X-CSRF-TOKEN": document.querySelector("meta[name=csrf]").content,
            },
            dataType: "json",
        })
            .then((resp) => {
                $(self).append(resp.view);
                self.__connectLiveView();
            })
            .fail((resp) => {
                console.error(resp);
                throw new Error("Unable to request view state");
            });
    }

    clearState() {
        throw new Error("Not yet implemented");
    }

    getEmptyMemento() {
        return {
            state_id: STATE_REQUEST_NEW,
        };
    }

    focus() {
        // currently nothing to do, as the view is entirely mouse driven
    }

    __doMerge(next) {
        return "no-merge";
    }

    /*
     * This function is a utility function that should only be called if the card being
     * viewed has been exported to static html, i.e. is not being rendered in the actual
     * ldcards application. For normal use cases inside of the application you should
     * add the code in the hooks section.
     *
     * Basically this should call all code that renders stuff in the view that happens
     * only at the client, and not on the server side. So relations, borders, dynamic
     * resizing etc.
     */
    renderResourcesStatic() {
        addScaledMargins(this);
        viewRelations.positionRelations(this);
        viewBorders.updateBorders(this);
        adjustTextSize(true, this);
    }
}

window.customElements.define(TAG_NAME, CardView);

function trackViewResize(viewArea, self) {
    let resizerBox = $(viewArea);
    let fullDocument = $(document);

    // add event to .resizer-box
    resizerBox.on("mousedown", ".resizer-box", (e) => {
        console.log("Track resize");
        let kind = e.target.classList.contains("top-left") ? "top-left" : "bottom-right";

        let initialWidth = resizerBox.width();
        let initialHeight = resizerBox.height();

        let lastValidWidth = initialWidth;
        let lastValidHeight = initialHeight;

        let lastX = e.screenX;
        let lastY = e.screenY;

        let requireRelationRedraw = false;

        // remember the positions of each card before we resize
        const cardIdToPosition = new Map();

        viewArea.querySelectorAll(".view-card").forEach((each) => {
            let position = utilModule.getBoundingClientRectRelativeToParent(each);
            cardIdToPosition.set(each.dataset.id, position);
        });

        function endResize() {
            // take value from css, not resizerBox.width(). This is required because the
            // effect of setting the last values using width() have not taken effect when
            // this event fires. So if we take the width() value, the box will resize after
            // persisting the value in the state for one last time
            let newWidth = parseInt(resizerBox.css("width"));
            let newHeight = parseInt(resizerBox.css("height"));

            // clear event - we don't need to track the resize anymore
            $(document).off("mousemove.resize");
            $(document).off("mouseup.resize");

            let newPositions = [];

            if (requireRelationRedraw) {
                newPositions = Array.from(viewArea.querySelectorAll(".view-card")).map((each) => {
                    let pos = utilModule.getBoundingClientRectRelativeToParent(each);

                    console.log("New positions", pos.top, pos.left);
                    return { id: each.dataset.id, x: pos.left, y: pos.top };
                });
            }

            console.log("Send resize event.");
            self.pushEvent("resize", {
                width: newWidth,
                height: newHeight,
                cardPositions: newPositions,
            });
        }

        // while resizing - adjust the current size
        // .resize allows us to add this event and remove it later on!
        fullDocument.on("mousemove.resize", (mouseMoveEvent) => {
            mouseMoveEvent.stopPropagation();
            mouseMoveEvent.preventDefault();

            // it is sometimes possible that the resize does not stop when the mouse button is released,
            // but I cannot reliably reproduce this. So this is a security abort that detects if no
            // button is pressed but a mouse move is still being handled, because that means that we
            // did not handle the mouse up event for some reason and thus this listener has not been
            // unsubscribed yet.
            if (mouseMoveEvent.originalEvent.buttons == 0) {
                console.warn(
                    "Detected mouse move event on resize but with no button down, aborting resize with current size!",
                );
                endResize();
                return;
            }

            let newX = mouseMoveEvent.screenX;
            let newY = mouseMoveEvent.screenY;

            let diffX = newX - lastX;
            let diffY = newY - lastY;

            // save current width, in case we need to reset the size because the box cannot
            // be made any smaller
            let currentWidth = Math.max(initialWidth + diffX, MIN_WIDTH);
            let currentHeight = Math.max(initialHeight + diffY, MIN_HEIGHT);

            // set new size
            resizerBox.width(currentWidth);
            resizerBox.height(currentHeight);

            // used to check if any cards are "exiting" the view area
            let topMax = false;
            let bottomMax = false;
            let leftMax = false;
            let rightMax = false;

            const cards = Array.from(viewArea.querySelectorAll(".view-card"));

            // for each cards inside the box
            cards.forEach((each) => {
                // Check if any cards are "exiting" the view area to the top or left
                // we need to consider the possible move of the card, as cards are
                // moved if resizing the view are in the top left corner
                let position = utilModule.getBoundingClientRectRelativeToParent(each);

                let outsideAtLeft = position.left < 0;
                if (outsideAtLeft) {
                    leftMax = true;
                }

                let outsideAtTop = position.top < 0;
                if (outsideAtTop) {
                    topMax = true;
                }

                // check if any cards are "exiting" the view are to the bottom or right
                // we must ignore the diffX value here, because cards are never moved
                // when resizing in the bottom right corner
                let right = position.left + position.width;
                let bottom = position.top + position.height;

                let outsideAtRight = right >= currentWidth;
                if (outsideAtRight) {
                    rightMax = true;
                }

                let outsideAtBottom = bottom >= currentHeight;
                if (outsideAtBottom) {
                    bottomMax = true;
                }
            });

            // check if we need to reset the height or width. Take the actual mouse movement into account,
            // otherwise it will not be possible to increase the size of the view area again after
            // a card has touched a border of the view area
            const resetHeight =
                (kind == "top-left" && topMax && diffY <= 0) || (kind == "bottom-right" && bottomMax && diffY <= 0);
            const resetWidth =
                (kind == "top-left" && leftMax && diffX <= 0) || (kind == "bottom-right" && rightMax && diffX <= 0);

            // reset both the width of the view area, as well as the computed new width and height
            // if no reset is necessary, and we are dragging the top left corner, move the cards
            if (resetWidth) {
                resizerBox.width(lastValidWidth);
            } else {
                // remember last valid width (so that we don't have to reset to original,
                // we can reset to last previous width)
                lastValidWidth = currentWidth;
            }

            if (resetHeight) {
                resizerBox.height(lastValidHeight);
            } else {
                lastValidHeight = currentHeight;
            }

            // only re-position if the position for all cards are valid
            if (kind == "top-left" && !leftMax && !topMax) {
                requireRelationRedraw = true;
                cards.forEach((each) => {
                    const card = $(each);
                    let position = cardIdToPosition.get(each.dataset.id);

                    card.css("top", Math.max(position.top + diffY, 0));
                    card.css("left", Math.max(position.left + diffX, 0));
                });
            }

            // if we've moved cards, we also need to redraw the relations
            if (requireRelationRedraw) {
                viewRelations.positionRelations(viewArea);
            }
        });

        fullDocument.on("mouseup.resize", (_x) => {
            endResize();
        });
    });
}

function addRemoveButton(viewArea, editor) {
    $(viewArea.parentElement)
        .find(".removeButton")
        .on("click", (e) => {
            e.preventDefault();
            console.log("Close button clicked");
            let userSaidDelete = confirm("Are you sure want to delete this view?");

            if (userSaidDelete) {
                ldcEvents.dispatch(ldcEvents.events.deleteEditor, editor, { instance: editor });
            }
        });
}

function addMoveCardListener(viewArea, self) {
    let dragTarget = null;
    let dragTargetChildren = [];
    let isSubViewCard = null;
    let isViewCard = null;

    let initialTop = null;
    let initialLeft = null;

    let currentX = null;
    let currentY = null;

    let initialClientX = null;
    let initialClientY = null;

    let maxX = null;
    let maxY = null;

    let scaleX, scaleY;

    function getChildren(dragTarget) {
        return viewUtils
            .getAllCards(viewArea)
            .filter((each) => each.dataset.id != dragTarget.dataset.id)
            .filter((each) => {
                return utilModule.fullyContains(dragTarget.getBoundingClientRect(), each.getBoundingClientRect());
            });
    }

    viewArea.addEventListener("mousedown", (e) => {
        if (e.button == 0) {
            // left click on the mouse
            const card = viewUtils.getCard(e);

            if (card != null) {
                dragTarget = card;
                dragTargetChildren = getChildren(dragTarget);
                isSubViewCard = dragTarget.classList.contains("subview-card");
                isViewCard = dragTarget.classList.contains("view-in-view-card");

                /**
                 * Some card can be scaled with transform-scale.
                 * In some cases, JS returns the scaled-dimensions, but expects the unscaled-dimensions to be set.
                 * This scaleX/scaleY is used to covert between those dimensions.
                 */
                [scaleX, scaleY] = utilModule.getScaleFactor(card);

                initialTop = parseInt(dragTarget.style.top);
                initialLeft = parseInt(dragTarget.style.left);

                currentX = initialLeft;
                currentY = initialTop;

                initialClientX = e.clientX;
                initialClientY = e.clientY;

                let marginTopBottom = parseFloat(dragTarget.style.marginTop);
                marginTopBottom = isNaN(marginTopBottom) ? 0 : marginTopBottom;
                marginTopBottom = marginTopBottom * 2; // for both sides

                let marginLeftRight = parseFloat(dragTarget.style.marginLeft);
                marginLeftRight = isNaN(marginLeftRight) ? 0 : marginLeftRight;
                marginLeftRight = marginLeftRight * 2; // for both sides

                // well where does the 3 come from? frankly, I don't know. It just looks consistent with 3
                // my theory where it comes from: the border left and right each 1px, then 1px distance from the box to the border.
                const magicConstant = 3;
                maxX =
                    viewArea.getBoundingClientRect().width - dragTarget.offsetWidth - marginLeftRight - magicConstant;
                maxY =
                    viewArea.getBoundingClientRect().height - dragTarget.offsetHeight - marginTopBottom - magicConstant;
                console.log({ maxX, maxY });

                // temporarily move the dragged card and its children to the end of the rendering list,
                // so that it is always visible. Will be reset by the update via Phoenix LiveView
                // automatically
                const allWrapper = Array.from(dragTargetChildren);
                allWrapper.unshift(dragTarget);

                // fix for #574: if we run this code immediately, a fast dblclick will not be registered.
                // Since all this does is putting the moved objects on the top of the render stack, we
                // can delay this for a moment without the user noticing (one will usually wait a short
                // moment before actually starting to move the object in a drag and drop operation, at
                // least that's my observation)
                setTimeout(() => {
                    // another fix because of the problem that we need to have click + move and dblclick
                    // events on arguments. If the user dblclicks (so he wants to open a linked argument),
                    // this code here runs first anyway, because if the user performs a dblclick, we still
                    // get the mousedown event first. So what can happen is that the user dblclicks, this
                    // triggers an update from LiveView, and then the objects in allWrapper will no longer
                    // be children of the viewArea, because we got the update from the LiveView. However
                    // we can detect the dblclick here, because in that case the dragTarget will have been
                    // set to null again from the mouseup event that follows the first and second mouse
                    // click, so in that case we do not try to shilft the children to the top of the
                    // render stack.
                    if (dragTarget != null) {
                        allWrapper.forEach((object) => {
                            const wrapper = object.closest("foreignObject");
                            viewArea.removeChild(wrapper);
                            viewArea.appendChild(wrapper);
                            wrapper.firstElementChild.style.zIndex = 100000;
                        });
                    }
                }, 250);

                cardViewUtils.removePopup();
            }
        }
    });

    const offsetToViewArea = (cardElement) => {
        // See if this card is within a subview
        let parentViewCard = utilModule.findParentWithSelector(cardElement.parentElement, ".view-card");

        let isInView = parentViewCard != null;
        let parent = isInView ? parentViewCard : cardElement.parentElement;

        let relativeToParent = utilModule.getBoundingClientRectRelativeTo(cardElement, parent);
        if (isInView) {
            const titleContent = $(parentViewCard).find(".content")[0];
            const padding = titleContent.clientHeight - 1;
            relativeToParent.y += padding;
            relativeToParent.x += padding;

            // if this is within view, we have to calcualte the relative offset
            let [x, y] = offsetToViewArea(parentViewCard);
            return [relativeToParent.x + x, relativeToParent.y + y];
        }

        return [relativeToParent.x, relativeToParent.y];
    };

    viewArea.addEventListener("mouseup", (e) => {
        endDrag(e);
    });

    // if the user drags the card outside of the viewArea, and then moves back in,
    // the card is still being moved, which can result in wrong positions outside
    // of the viewArea. So we simply stop moving the card as soon as the mouse leaves
    // the viewArea
    viewArea.addEventListener("mouseleave", (e) => {
        endDrag(e);
    });

    function endDrag(e) {
        if (dragTarget != null) {
            const meta = {
                id: dragTarget.getAttribute("data-id"),
                deltaX: parseInt(dragTarget.style.left) - initialLeft,
                deltaY: parseInt(dragTarget.style.top) - initialTop,
            };

            // If this move operation was actually part of a dblclick (we allow both dblclicks to open
            // a linked card from an argument and a mousedown + move on arguments), and the card was thus
            // never moved, do __not__ send an "object-move-end" event to the server. That event will
            // trigger an update of the LiveView, which in turn will change the objects in the viewArea,
            // and that can lead to a "jumping around" of the objects inside a subview on the second part
            // of the dblclick, because then the acutally clicked object will have been replaced and
            // thus incorrect inital x and y values will lead to incorrect deltaX and deltaY values
            // here.
            if (meta.deltaX != 0 || meta.deltaY != 0) {
                let intersectingSubviews = viewUtils.getIntersectingView(viewArea, dragTarget);
                console.log("New subviews", intersectingSubviews);
                // we take the first "subview" the card is moved into
                meta["newSubviewIds"] = intersectingSubviews.map((each) => each.dataset.id);
                meta["insideCardIds"] = utilModule.removeGrandChildren(getChildren(dragTarget)).map((each) => {
                    return {
                        id: each.dataset.id,
                        inViews: viewUtils.getIntersectingView(viewArea, each).map((each) => each.dataset.id),
                    };
                });

                // // In case the card gets moved into a new view, we have to consider the scaling
                if (intersectingSubviews.length > 0) {
                    let [scaleViewX, scaleViewY] = utilModule.getScaleFactor(intersectingSubviews[0]);
                    meta.x = Math.round(meta.x / scaleViewX);
                    meta.y = Math.round(meta.y / scaleViewY);
                }

                console.log("object-move-end", meta);
                self.pushEvent("object-move-end", meta);
            }

            dragTarget = null;
        }
    }

    viewArea.addEventListener("mousemove", (e) => {
        if (dragTarget != null) {
            let newX = initialLeft + (e.clientX - initialClientX);
            let newY = initialTop + (e.clientY - initialClientY);

            newY = utilModule.clamp(newY, 1, maxY);
            newX = utilModule.clamp(newX, 1, maxX);

            let deltaX = newX - currentX;
            let deltaY = newY - currentY;

            dragTarget.style.top = `${newY}px`;
            dragTarget.style.left = `${newX}px`;

            currentX = newX;
            currentY = newY;

            if (deltaX != 0 && dragTargetChildren.length > 0) {
                dragTargetChildren.forEach((child) => {
                    child.style.left = `${parseInt(child.style.left) + deltaX}px`;
                });
            }

            if (deltaY != 0 && dragTargetChildren.length > 0) {
                dragTargetChildren.forEach((child) => {
                    child.style.top = `${parseInt(child.style.top) + deltaY}px`;
                });
            }

            if (isViewCard) {
                viewRelations.positionRelations(viewArea, [dragTarget]);
                viewRelations.positionRelations(viewArea, dragTargetChildren);
            } else {
                viewRelations.positionRelations(viewArea, [dragTarget]);
            }
        }
    });
}

function addScaledMargins(viewArea) {
    viewArea.querySelectorAll(".view-card:not([data-scale='1.0'])").forEach((scaledCard) => {
        const bounds = scaledCard.getBoundingClientRect();

        const leftRight = (-1 * (scaledCard.offsetWidth - bounds.width)) / 2;
        const topBottom = (-1 * (scaledCard.offsetHeight - bounds.height)) / 2;

        scaledCard.style.margin = `${topBottom}px ${leftRight}px`;
    });
}

/**
 * Solves #468 automatically resize text if it overlaps border
 * The idea was to reduce the font-size until it fits the content.
 */
function adjustTextSize(readOnly, viewArea) {
    // Only adjust the text size in readOnly mode, not when user is editing the card.
    // The latter situation can lead to the problem that the user manually adjusts the borders
    // until they are just right for the text, however the text has been downscaled when
    // loading the card already, so loading it again makes it even smaller etc, until
    // the cutoff limit has been reached. If you then view the card, no more adjustments
    // to make it smaller are possible, and this whole trick here does nothing
    if (readOnly) {
        viewArea.querySelectorAll(".view-card").forEach((card) => {
            let content = card.querySelector(".content");
            let isBeingEdited = card.classList.contains("edit-name");

            // bugfix 489: if card name is edit, they shrunk too tiny
            if (!isBeingEdited) {
                const minFontSize = 89;
                let fontSize = 100;
                // as long as content too larger and fontSize not too small
                while (
                    !utilModule.fullyContains(card.getBoundingClientRect(), content.getBoundingClientRect()) &&
                    fontSize > minFontSize
                ) {
                    content.style.fontSize = `${fontSize}%`;
                    // by setting the hyphens to auto, we allow hyphen-breaks and larger font size
                    content.style.hyphens = "auto";

                    // Bug 488: if font-size is too small, one can not read the text, so min value is 5
                    fontSize = Math.max(fontSize - 5, minFontSize);
                }
            }
        });
    }
}

/**
 * We needed to highlight all "duplicated" cards and the original one.
 * The server sets a unique id for all of them. This functions adds a listener to check if someone hovers over
 * such a card, and then sets the background color
 */
function highlightDuplicateGroup(viewArea) {
    let cardsWithDuplicateCardId = Array.from(viewArea.querySelectorAll(".view-card"))
        // has duplicate group id
        .filter((each) => {
            if ("duplicateGroupId" in each.dataset) {
                return each.dataset.duplicateGroupId !== "";
            }
            return false;
        });

    cardsWithDuplicateCardId.forEach((card) => {
        // on enter change the color of all objects with the same id
        card.addEventListener("mouseenter", (each) => {
            let duplicateGroupId = each.target.dataset.duplicateGroupId;
            if (duplicateGroupId) {
                // for all cards
                cardsWithDuplicateCardId.forEach((found) => {
                    // has same id
                    if (found.dataset.duplicateGroupId === duplicateGroupId) {
                        let content = found.querySelector(".content");
                        // this is some nice-looking blueish color
                        content.style.backgroundColor = "rgba(147, 202, 237, 0.42)";
                    }
                });
            }
        });

        card.addEventListener("mouseleave", (each) => {
            cardsWithDuplicateCardId.forEach((found) => {
                let content = found.querySelector(".content");
                content.style.backgroundColor = "";
            });
        });
    });
}

/**
 * Let me explain:
 * In order to add cards to realms (also called opinions). You click on the "link" button on
 * the opinion, while the "linking" is active you can click on cards.
 * Now when you are done, you either click somewhere inside the view (but not on an argument)
 * or you click on "Done". The request is now you should be able to "click outside of view" as well
 * to trigger the "Done".
 * Understood?
 *
 * This functions check  if there is a linking-processing going on, in that case, it adds a global
 * listener.
 */
function checkForRealmUnlinkButton(self, toolbarArea) {
    let viewArea = toolbarArea.parentElement.querySelector("#view-area");
    let unlinkButton = toolbarArea.querySelector("#realm-unlink-button");

    let hasUnlinkButton = unlinkButton != null;
    const listenerName = "mousedown.unlinkButton";
    if (hasUnlinkButton) {
        console.log("Found unlink button");
        // we add a listener with name
        $(document).on(listenerName, (e) => {
            let insideViewArea = viewArea.contains(e.target);
            // this is a bit stupid, but without this part you can't open
            // the "link" button, because the press is interpreted as outside of viewArea and hence
            // immediately closed
            let insideToolBar = toolbarArea.contains(e.target);

            if (!insideViewArea && !insideToolBar) {
                console.log("Reset, because clicked outside");
                self.pushEvent("esc-pressed", {});
            }
        });
    } else {
        console.log("Remove unlink button listener");
        $(document).off(listenerName);
    }
}

// can be module level because all info must be provided externally
function copyPasteMemento() {
    return {
        state_id: STATE_REQUEST_FROM_COPY,
    };
}

function addRealmDragAndDrop(self) {
    let realmsParent = self.el.querySelector(".realms");
    dragDrop.addDragAndDrop(realmsParent, (tabId, insertBeforeId) => {
        self.pushEvent("realm-reorder", {
            tabId: tabId,
            insertBeforeId: insertBeforeId,
        });
    });
}

function isHorizontalLine(firstCardRect, secondCardRect) {
    return (
        horizontallyInside(firstCardRect, secondCardRect.top) ||
        horizontallyInside(firstCardRect, secondCardRect.bottom) ||
        horizontallyInside(secondCardRect, firstCardRect.top) ||
        horizontallyInside(secondCardRect, firstCardRect.bottom)
    );
}
function horizontallyInside(rect, point) {
    return rect.top <= point && point <= rect.bottom;
}

function isVerticalLine(firstCardRect, secondCardRect) {
    return (
        verticallyInside(firstCardRect, secondCardRect.left) ||
        verticallyInside(firstCardRect, secondCardRect.right) ||
        verticallyInside(secondCardRect, firstCardRect.left) ||
        verticallyInside(secondCardRect, firstCardRect.right)
    );
}
function verticallyInside(rect, point) {
    return rect.left <= point && point <= rect.right;
}

function clip(value, min, max) {
    if (value < min) {
        return min;
    }
    if (value > max) {
        return max;
    }
    return value;
}

// aka align line
function makeLineStraight(self, viewArea, alignLine) {
    alignLine.addEventListener("click", (e) => {
        const pushToServer = (fromOrTo, x, y) => {
            console.log("Push to server", { fromOrTo, x, y });
            self.pushEvent("object-move-end", {
                id: alignLine.dataset.id,
                from_to: fromOrTo,
                target: "card",
                x: x,
                y: y,
            });
        };

        const fromCardId = alignLine.dataset.fromId;
        const toCardId = alignLine.dataset.toId;
        const id = alignLine.dataset.id;

        const fromCard = viewArea.querySelector(`.view-card[data-id="${fromCardId}"]`);
        const toCard = viewArea.querySelector(`.view-card[data-id="${toCardId}"]`);
        const lineBeingAligned = viewArea.querySelector(`.relation-shadow-group[data-id="${id}"]`);

        const viewBounding = viewArea.getBoundingClientRect();
        console.debug("Cards: ", { fromCard, toCard, area: viewArea.getBoundingClientRect() });

        let fromCardRect = fromCard.getBoundingClientRect();
        let toCardRect = toCard.getBoundingClientRect();

        /**
         * TBH: I have no idea why this is exactly one pixel!
         * What happened was that the line was not perfectly straight, and you can see it if you really looked closely.
         * I debugged, and my suspicions was there was some padding I didn't consider. I experienced with some padding, and
         * 1 is the one which worked. Until now I have no clue why exactly that value.
         */
        const magicConstantFix = 1;

        if (isHorizontalLine(fromCardRect, toCardRect)) {
            /**
             *  [fromCard] - [toCard]
             */
            let leftCard = {
                rect: fromCardRect,
                fromOrTo: "from",
                lineTouchingY: Number(lineBeingAligned.dataset.fromY),
                lineTouchingX: Number(lineBeingAligned.dataset.fromX),
            };
            let rightCard = {
                rect: toCardRect,
                fromOrTo: "to",
                lineTouchingY: Number(lineBeingAligned.dataset.toY),
                lineTouchingX: Number(lineBeingAligned.dataset.toX),
            };

            // check if we need to swap left and right
            if (fromCardRect.right > toCardRect.left) {
                console.log("Swap left and right");
                [leftCard, rightCard] = [rightCard, leftCard];
            }

            let lineLeftX = Math.min(leftCard.lineTouchingX, rightCard.lineTouchingX);
            let lineMiddleX = lineLeftX + Math.abs(leftCard.lineTouchingX - rightCard.lineTouchingX) / 2;
            // the line coordinates are relative to the view, but the click position is relative to the client view port
            lineMiddleX += viewBounding.left;

            // decide if user clicked on the left half or right half of the line
            // We are interested, where the user initially clicked (not e.g. where popup is open right now)
            if (alignLine.dataset.popupX < lineMiddleX) {
                console.log("User clicked on left half, so change right point");

                let spaceToTop = leftCard.lineTouchingY + viewBounding.top - rightCard.rect.top + magicConstantFix;
                let y = clip(spaceToTop / rightCard.rect.height, 0, 1);
                let x = leftCard.lineTouchingX < rightCard.lineTouchingX ? 0 : 1;
                pushToServer(rightCard.fromOrTo, x, y);
            } else {
                console.log("User clicked on right half, so change left point");
                let spaceToTop = rightCard.lineTouchingY + viewBounding.top - leftCard.rect.top + magicConstantFix;
                let x = leftCard.lineTouchingX < rightCard.lineTouchingX ? 1 : 0;
                let y = clip(spaceToTop / leftCard.rect.height, 0, 1);
                pushToServer(leftCard.fromOrTo, x, y);
            }
        } else if (isVerticalLine(fromCardRect, toCardRect)) {
            /**
             *  [fromCard]
             *       |
             *      [toCard]
             */
            console.log("Top / Bottom");
            let topCard = {
                rect: fromCardRect,
                fromOrTo: "from",
                lineTouchingY: Number(lineBeingAligned.dataset.fromY),
                lineTouchingX: Number(lineBeingAligned.dataset.fromX),
            };
            let bottomCard = {
                rect: toCardRect,
                fromOrTo: "to",
                lineTouchingY: Number(lineBeingAligned.dataset.toY),
                lineTouchingX: Number(lineBeingAligned.dataset.toX),
            };

            // check if we need to swap left and right
            if (fromCardRect.bottom > toCardRect.top) {
                console.log("Swap bottom and right");
                [topCard, bottomCard] = [bottomCard, topCard];
            }

            let lineTopY = Math.min(topCard.lineTouchingY, bottomCard.lineTouchingY);
            let lineMiddleY = lineTopY + Math.abs(topCard.lineTouchingY - bottomCard.lineTouchingY) / 2;
            // the line coordinates are relative to the view, but the click position is relative to the client view port
            lineMiddleY += viewBounding.top;

            // decide if user clicked on the top half or bottom half of the line
            // We are interested, where the user initially clicked (not e.g. where popup is open right now)
            if (alignLine.dataset.popupY < lineMiddleY) {
                console.log("User clicked on top half, so change bottom point");

                let spaceToLeft = topCard.lineTouchingX + viewBounding.left - bottomCard.rect.left + magicConstantFix;
                let x = clip(spaceToLeft / bottomCard.rect.width, 0, 1);
                let y = topCard.lineTouchingY < bottomCard.lineTouchingY ? 0 : 1;
                pushToServer(bottomCard.fromOrTo, x, y);
            } else {
                console.log("User clicked on bottom half, so change top point");
                let spaceToTop = bottomCard.lineTouchingX + viewBounding.left - topCard.rect.left + magicConstantFix;
                let x = clip(spaceToTop / topCard.rect.width, 0, 1);
                let y = topCard.lineTouchingY < bottomCard.lineTouchingY ? 1 : 0;
                pushToServer(topCard.fromOrTo, x, y);
            }
        } else {
            console.log("Rest");
        }

        cardViewUtils.removePopup();
    });
}

/**
 * We want to create a local card with CTR-N
 */
function createNewCardOnKeyPress(viewArea, self) {
    // remember the mouse position, as the keydown does not have access to it
    let currentX;
    let currentY;
    viewArea.addEventListener("mousemove", (event) => {
        currentX = event.offsetX;
        currentY = event.offsetY;
    });

    // note: this only works, because we have tabindex=0 on the parent-div
    let parent = utilModule.findParentWithSelector(viewArea, ".card-view-editor");
    parent.addEventListener("keydown", (event) => {
        // on alt+N or CTRL+N
        // console.log(`event.altKey=${event.altKey} / event.ctrlKey=${event.ctrlKey}`, event.key)
        // well why both? because on windows CTRL+N does not work, on Mac Alt+N does not work
        // so, this was the solution
        if ((event.ctrlKey || event.altKey) && event.key == "n") {
            let data = {
                x: String(currentX), // backend expects string
                y: String(currentY),
            };
            console.log("Alt+N/CTRL+N  clicked, create event", data);
            self.pushEvent("create-local-card", data);
        }
    });
}

/**
 * We want views to be resizable, but it's hard to know where to put the markers.
 * We also don't want to show the border all the time. Our solution:
 * Show the border in edit mode only and only on hover.
 */
function makeTextInViewResizable(viewArea) {
    console.log("Make text in view resizable");
    const readOnly = utilModule.parseBool(viewArea.dataset.readOnly);

    // only if you edit the view
    if (!readOnly) {
        viewArea.querySelectorAll(".view-card.text").forEach((each) => {
            each.classList.add("hover:outline");
            each.classList.add("hover:outline-1");
        });
    }
}

function openCardByTitle(editor, title) {
    cardModule.findCardsByTitle(title, (found) => {
        if (found.length === 1) {
            console.info("Found one card: ", found[0]);
            fireShowCardEvent(
                editor,
                found[0].id,
                "true", // if we found a card, then it means it's accessible
                e.shiftKey,
                keyModule.ctrlMeta(e),
            );
        } else if (found.length > 1) {
            console.warn("More than one found. something went wrong!", title);
        }
    });
}

function fireShowCardEvent(
    editor,
    instanceId,
    instance_accessible,
    openInPartner,
    inNewWindow,
    scrollToElementSelector,
) {
    if (instance_accessible === "true") {
        editor.__notify(editor.SUBSCRIPTION_TARGETS.showInPartner, {
            instanceId: instanceId,
            openInPartner: openInPartner,
            inNewWindow: inNewWindow,
            scrollToElementSelector: scrollToElementSelector,
        });
    } else {
        alertModule.danger(document, "This linked card cannot be opened because it has not been shared with you.");
    }
}

/**
 * User clicked on triangle, so depending on the triangle - perform the action
 */
function clickedOnTriangle(event, editor, cardTriangle, viewArea, self) {
    if (cardTriangle.matches(".external-link")) {
        const href = cardTriangle.dataset.href;
        if (href != "") {
            utilModule.openLink(href);
        }
    }

    if (cardTriangle.matches(".card-link")) {
        const instanceAccessible = cardTriangle.dataset.instanceAccessible;
        fireShowCardEvent(
            editor,
            cardTriangle.dataset.instanceId,
            instanceAccessible,
            event.shiftKey,
            keyModule.ctrlMeta(event),
        );
    }

    if (cardTriangle.matches(".anchor-link")) {
        let cardId = cardTriangle.dataset.instanceId;
        let anchorId = cardTriangle.dataset.anchorId;
        // does cardId == the current card we are in?
        let sameCard = cardTriangle.dataset.sameCard !== undefined;

        const openInPartner = event.shiftKey;
        const inNewWindow = keyModule.ctrlMeta(event);
        const targetAnchor = `ldc-link-destination[id='${anchorId}']`;
        if (!(openInPartner || inNewWindow) && sameCard) {
            console.log("Same card, so scroll to the position.");
            waitForVisibleElement(targetAnchor, (highlightedElement) => {
                highlightIfInViewPort([highlightedElement]);
            }).catch((error) => {
                console.error(error);
                alerts.danger(document, "Anchor text location not found.");
                console.log("Element not found!");
            });
        } else {
            console.log(`Anchor card, open anchor point anchorId=${anchorId} cardId=${cardId}`);
            ldcEvents.displayCard(editor, cardId, {
                openInPartner: openInPartner,
                inNewWindow: inNewWindow,
                // Scroll to the target link destination
                scrollToElementSelector: targetAnchor,
            });
        }
    }

    // aka doorlet
    if (cardTriangle.matches(".pass-link")) {
        console.log("double click on pass-link");
        let singleInstanceId = cardTriangle.dataset.singlePassedCardId;
        console.log("singleInstanceId", singleInstanceId, cardTriangle, cardTriangle.dataset);
        if (singleInstanceId) {
            console.log("single instance", singleInstanceId);
            const object_id = cardTriangle.dataset.singlePassedObjectId;
            const scrollToElementSelector = `.view-card[data-instance-id='${object_id}']`;
            fireShowCardEvent(
                editor,
                singleInstanceId,
                "true",
                event.shiftKey,
                keyModule.ctrlMeta(event),
                scrollToElementSelector,
            );
        } else {
            // which means there are multiple, so show card-pass
            let data = {
                x: event.clientX,
                y: event.clientY,
                offsetX: event.offsetX,
                offsetY: event.offsetY,
            };

            onContextMenu(self, viewArea, event.target, data);
        }
    }
}

function clickedOnRelation(e, editor) {
    // check if the user clicked on a relation, mainly to open a link
    let dataset = e.target.dataset;
    if (dataset.anchorId) {
        let cardId = dataset.anchorTargetCardId;
        let anchorId = dataset.anchorId;
        const targetAnchor = `ldc-link-destination[id='${anchorId}']`;

        console.log("Anchor card link from relation, open anchor point ", anchorId);
        ldcEvents.displayCard(editor, cardId, {
            openInPartner: e.shiftKey,
            inNewWindow: keyModule.ctrlMeta(e),
            scrollToElementSelector: targetAnchor,
        });
    } else {
        console.debug("relation has no anchor id");
    }
}

/**
 * Handles right-click / context click logic
 */
const onContextMenu = (self, viewArea, target, data) => {
    // we didn't click on the view-area
    if (target !== viewArea) {
        // check if it was a card
        let foundCard = utilModule.findParentWithSelector(target, ".view-card");

        if (foundCard != null) {
            data.id = foundCard.dataset.id;

            if (target.matches(".link-triangle")) {
                data.corner = target.dataset.corner;
                data.offsetX += foundCard.getBoundingClientRect().width;
            }

            if (target.matches(".card-link")) {
                data.corner = target.dataset.corner;
                data.offsetY = foundCard.getBoundingClientRect().height;
            }

            if (target.matches(".anchor-link")) {
                data.corner = target.dataset.corner;
                data.offsetX -= foundCard.getBoundingClientRect().width;
                data.offsetY += foundCard.getBoundingClientRect().height;
            }

            if (target.matches(".pass-link")) {
                data.corner = target.dataset.corner;
                data.offsetX -= foundCard.getBoundingClientRect().width;
            }

            if (
                target.matches(".loc-top-right") ||
                target.matches(".loc-bottom-right") ||
                target.matches(".loc-bottom-left") ||
                target.matches(".loc-top-left")
            ) {
                data.corner = target.dataset.corner;
            }
        }
    }

    // for some unknown reason, if we open the context menu of a card pass, and then the context
    // menu of another card pass directly after it in the same view, the content of the popup
    // will not be updated by LiveView. It does render the content, even with the new assigns,
    // but does not actually produce the new output and will instead create the same output
    // as before, and therefore not send an update. Note that this happens only in view mode
    // of a card, not edit mode, that's why we can ignore this event in edit mode
    //
    // The only solution I've found is to send an event right _before_ sending the actual
    // event to open the context menu that clears the popup_for assign. Ugly, but at least
    // this works as intended now
    self.pushEvent("pre-context-menu-click", {});

    console.log("on-context-menu-click", data);
    self.pushEvent("on-context-menu-click", data);
};

/**
 * This function adds dragging cards from one editor and dropping them inside a view
 * which will convert them to an argument linked with a card.
 * This feature is for now disabled (#1457) as the clients wish.
 * This feature was removed on the 30.June 2026 - if it's there in few years, remove the code.
 */
function addDragAndDropIntoView(viewArea, self) {
    // accept all drop events because of the way how chromium handles the
    // getData method in dragover events
    viewArea.addEventListener("dragover", (e) => {
        e.preventDefault();
    });

    viewArea.addEventListener("drop", (e) => {
        const ids = e.dataTransfer.getData("ldc/instanceids");

        if (ids != null && ids != "") {
            try {
                const cards = JSON.parse(ids);
                // only prevent default after trying to parse JSON, otherwise this can
                // interfere with other drag and drop use cases
                e.preventDefault();

                let data = {
                    cardids: cards.map((card) => card.id),
                    x: e.offsetX,
                    y: e.offsetY,
                };

                const parentCard = e.target.parentElement;

                // check if we are adding the card to a subview
                if (parentCard != null && parentCard.classList.contains("view-in-view-card")) {
                    data.viewId = parentCard.dataset.id;
                    console.log(`Drop card to view: ${data.viewId}`);
                } else if (e.target === viewArea) {
                    console.log("Added to viewArea");
                    data.viewId = null; // no view, we just add to the root
                } else {
                    // This means we try to add on top of a normal-card
                    alertModule.danger(document, "Can't drop card on another card");
                    return;
                }

                console.log(`Add-cards with data:`, data);
                self.pushEvent("add-cards", data);
            } catch (error) {
                console.error("Unable to parse instanceids for drop in card view");
                console.error(error);
            }
        }
    });
}

// Views have a fixed width in px and get cut off on paper, so scale them down to the printable width.
// --print-zoom is only used in print.css (@media print).
// to test with Chrome: Developer Console -> press esc -> Rendering -> Emulate css media type -> print
const PRINTABLE_WIDTH = 650; // just a random px which worked for me
function setPrintZoom(viewArea) {
    // use the inline width, not getBoundingClientRect, because the zoom itself changes the rect in print media
    const width = parseFloat(viewArea.style.width);
    if (width > 0) {
        // search --print-zoom in print.css
        viewArea.style.setProperty("--print-zoom", Math.min(1, PRINTABLE_WIDTH / width));
    }
}

export const hooks = {
    ViewAreaHook: {
        mounted() {
            const self = this;
            const viewArea = self.el;
            const editor = utilModule.findParentWithSelector(viewArea, TAG_NAME);

            const readOnly = utilModule.parseBool(viewArea.dataset.readOnly);

            addRemoveButton(viewArea, editor);
            // cleanup any left-over popup
            cardViewUtils.removePopup();

            editor.addEventListener("cardTypeChanged", () => {
                self.pushEvent("change-cardtype", {
                    type: editor.__type,
                });
            });

            /**
             * Logic when you click on a card / click on an argument.
             */
            viewArea.addEventListener("click", (e) => {
                /**
                 * The user clicks double sometimes, wanted the same behaviour.
                 * This checks if there was a "double-click" and ignores the second click.
                 */
                if (e.detail > 1) {
                    // ignore the second click of a double click, the first one already triggered the action
                    return;
                }

                const cardTriangle = viewUtils.getTriangleAtPosition(e);
                // we determine the triangle first, and if user clicked on that, then trigger that action
                if (cardTriangle) {
                    clickedOnTriangle(e, editor, cardTriangle, viewArea, self);
                }
                // Disabled "Click on card should not do anything"-Feature, but I think it will come back, becasue it's not user-friendly.
                // If this is still here in 2027, then remove the below code. #1502
                /*
                else {
                    // if not, user clicked on the card -
                    const card = viewUtils.getCard(e);
                    if (card != null) {
                        // check if that card has any triangles
                        let foundTriangles = card.querySelectorAll(".link-triangle");
                        console.log("Found triangles:", foundTriangles.length);

                        // Basically if there is only one link, then double-click on the card should trigger that link
                        // but if there are multiple, then the user has to click on either triangles.
                        if (foundTriangles.length === 1) {
                            clickedOnTriangle(e, editor, foundTriangles[0], viewArea, self);
                        } else {
                            // This part is only here out of legacy concerns.
                            // We are migrating all logic to "links" and
                            // Hopefully this will disappear in near future (hope begins in February 2025)
                            if (card.matches(".has-no-instance") || card.matches(".view-only-card")) {
                                // Requirement changed: if card has no specific id, then open the card with the same title
                                openCardByTitle(card.dataset.title);
                            }
                        }
                    } else {
                        clickedOnRelation(e, editor);
                    }
                }
               */
            });

            if (!readOnly) {
                trackViewResize(viewArea, self);
                addMoveCardListener(viewArea, self);

                viewRelations.registerDragEvents(viewArea, self);
                viewBorders.registerCardResizing(self);

                // we show a hidden-border around text elements, show the border on hover
                viewArea.querySelectorAll(".view-card.text").forEach((each) => {
                    each.classList.add("hover:outline");
                    each.classList.add("hover:outline-1");
                });

                // addDragAndDropIntoView(viewArea, self);
            }

            viewArea.addEventListener("click", (e) => {
                const linksTo = e.target.getAttribute("links_to");

                if (linksTo != null) {
                    editor.__notify(editor.SUBSCRIPTION_TARGETS.showInPartner, {
                        instanceId: linksTo,
                        openInPartner: e.shiftKey,
                        inNewWindow: keyModule.ctrlMeta(e),
                    });
                }
            });

            // listening on viewArea is not reliable, so we simply listen on the document
            // that will close the popups on any esc press, but that is ok, because it is rather
            // unlikely that we want multiple popups at the same time open in the app
            document.addEventListener("keydown", (e) => {
                if (event.which == keys.key_esc) {
                    console.log("ESC pressed: send event to backend");
                    self.pushEvent("esc-pressed", {});
                }

                if (event.which == keys.key_f2) {
                    console.log("F2 pressed: send event to backend");
                    self.pushEvent("on-keyboard-event", {
                        key: "f2",
                    });
                }
            });

            // moved the context menu logic to JS from LiveView, because it was easier to find
            // the card affected via JS than via HTML tricks
            viewArea.addEventListener("contextmenu", (e) => {
                e.preventDefault();
                console.log("===>", e.target);
                let data = {
                    x: e.clientX,
                    y: e.clientY,
                    offsetX: e.offsetX,
                    offsetY: e.offsetY,
                };

                onContextMenu(self, viewArea, e.target, data);
            });
            if (window.ldc.touchDevice === true) {
                viewArea.addEventListener("long-press", (e) => {
                    e.preventDefault();

                    let abc = e.target.getBoundingClientRect();
                    let data = {
                        x: e.detail.clientX,
                        y: e.detail.clientY,
                        offsetX: e.detail.clientX - abc.left,
                        offsetY: e.detail.clientY - abc.top,
                    };

                    onContextMenu(self, viewArea, e.target, data);
                });
            }

            addScaledMargins(this.el);
            viewRelations.positionRelations(this.el);
            viewBorders.updateBorders(this.el);
            adjustTextSize(readOnly, this.el);
            highlightDuplicateGroup(this.el);
            viewArea.__liveViewElement = this;
            createNewCardOnKeyPress(this.el, self);
            makeTextInViewResizable(this.el);
            setPrintZoom(this.el);
        },
        updated() {
            let self = this;
            const viewArea = this.el;
            const readOnly = utilModule.parseBool(viewArea.dataset.readOnly);
            // LiveView re-renders the style attribute, which drops --print-zoom, so set it again
            setPrintZoom(viewArea);

            // Trigger autosave when view content is updated
            const editor = utilModule.findParentWithSelector(viewArea, TAG_NAME);
            if (editor && !readOnly) {
                ldcEvents.dispatch(ldcEvents.events.editorContentChanged, editor);
            }

            addScaledMargins(this.el);
            viewRelations.positionRelations(this.el);
            viewBorders.updateBorders(this.el);
            adjustTextSize(readOnly, viewArea);
            highlightDuplicateGroup(this.el);
            makeTextInViewResizable(this.el);

            const focusOn = this.el.querySelector(".request-focus");
            if (focusOn != null) {
                focusOn.focus();

                if (focusOn.classList.contains("request-select")) {
                    focusOn.select();
                }
            }

            // some popups cannot be attached to a html object (like the menus for relations),
            // so we want to position them at the point of the mouse click. However the phx-binding
            // does not allow us to retrieve the local to view-area mouse coordinates, so we need
            // to correct the positioning after loading
            // in theory we could simply position them relative to the body, however there are too
            // many other parents that already set the position=relative attribute before we reach
            // the body
            const floatingPopup = this.el.querySelector(".hover-menu.free-float");
            if (floatingPopup != null) {
                let top = Number.parseInt(floatingPopup.style.top);
                const left = Number.parseInt(floatingPopup.style.left);

                const bounds = utilModule.boundingClientRelToBody(this.el);

                floatingPopup.style.left = left - bounds.left + "px";

                // check if we need to change the floating position of the popup from top to
                // bottom, happens if it is in the lower third of the view
                top = top - bounds.top;

                if (top / bounds.height > 0.66) {
                    floatingPopup.style.top = null;
                    floatingPopup.style.bottom = `${bounds.height - top}px`;
                } else {
                    floatingPopup.style.top = top + "px";
                }
            }

            // We must move any popups to the body because otherwise large popups are not completely
            // visible in very small views and thus become unusable. The simpler solution to simply
            // set a overflow: visible on the view is not possible, because then cards that are
            // outside of the borders of the view become visible as well (which should be hidden and
            // only being seen if scrolled to them)
            const hoverMenu = this.el.querySelector(".hover-menu");
            cardViewUtils.removePopup();

            if (hoverMenu != null) {
                console.log("Detach popup from original parent and put it in root");

                const relative = utilModule.getBoundingClientRectRelativeTo(hoverMenu, document.body);
                const originalParent = hoverMenu.parentElement;

                const top = relative.top < 10 ? 10 : relative.top;
                const left = relative.left < 10 ? 10 : relative.left;

                hoverMenu.style.transform = "";
                hoverMenu.style.top = `${top}px`;
                hoverMenu.style.left = `${left}px`;
                hoverMenu.style.right = "";
                hoverMenu.style.bottom = "";

                hoverMenu.parentElement.removeChild(hoverMenu);

                // this is an ugly hack that is required because of the way how LiveView works: In
                // order to still be able to capture the clicks on the buttons as phoenix events,
                // the LiveView js code must find which phx-view this element belongs to. This is done
                // by moving upwards in the DOM until we find a node with the phx-attributes etc.
                // However, by moving the popup to the body, we will no longer find that information so
                // phoenix will never send the event. In order to circumvent this problem, we simply make
                // a clone of the "phx-node" of this view, and put the popup into it, so that the
                // JS code of LiveView is able to find the LiveView id.
                const cloned = self.__view.el.cloneNode(false);
                cloned.appendChild(hoverMenu);
                document.body.appendChild(cloned);

                window.ldc.viewPopup = cloned;
                // For some events we need the original parent!
                window.ldc.viewPopupOriginalParent = originalParent;

                const relationScale = cloned.querySelector("#hover-menu-relation input#relation-scale");
                if (relationScale != null) {
                    const id = relationScale.dataset.id;
                    const path = this.el.querySelector(`g[data-id="${id}"] path.view-relation`);

                    relationScale.addEventListener("input", (e) => {
                        path.style.strokeWidth = e.target.value;
                    });

                    relationScale.addEventListener("change", pushNewScale(self, id));
                }

                /**
                 * The functionality of aligning a line is implemented in JS, because it's easier that way
                 */
                const alignLine = cloned.querySelector("#align-line");
                if (alignLine != null) {
                    makeLineStraight(self, this.el, alignLine);
                }
            }

            // If users added a new card through view, the card should be immediately opened on the
            // second editor: this triggers a event to show the new card on the second editor
            const newlyCreatedCardId = this.el.getAttribute("data-new-card-id");
            if (newlyCreatedCardId) {
                // function triggered upon card saved ... updated server with new card
                const cardIsSaved = function (savedCard) {
                    self.pushEvent("refresh-card", { cardId: savedCard.id });
                };

                ldcEvents.dispatch(
                    ldcEvents.events.editInPartnerView,
                    this.el,
                    {
                        cardId: newlyCreatedCardId,
                        onCardSaved: cardIsSaved,
                    },
                    { openInPartner: true },
                );
            }

            // Since we have to show a popup, we decided to implement this event in JS
            // reason for document.body and not this.el => the popup is attached to root each time you open the context menu
            const linkMenu = document.body.querySelector("#link-card-menu");
            if (linkMenu) {
                linkMenu.addEventListener("click", (event) => {
                    // ensure that all popups are deleted
                    cardViewUtils.removePopup();

                    searchPopup.show({ multiSelect: false }).done((selectedCards) => {
                        // can only have one result cause we've set multiSelect to false
                        const foundId = selectedCards[0].id;
                        const opts = {
                            id: linkMenu.dataset.id,
                            linkedCardId: foundId,
                        };
                        console.log("Link local card", opts);
                        self.pushEvent("link-local-card", opts);
                    });
                });
            }

            anchorLinkManager.addAnchorLinkFeatureInView(document.body, self);

            // We moved the "delete-card" logic from pure LiveView to here, so that we can do some actions before
            let removeCardButton = document.body.querySelector(".remove-card-button");
            if (removeCardButton) {
                removeCardButton.addEventListener("click", () => {
                    confirmModule
                        .confirmDangerousAction({
                            message: "Do you want to delete this argument?",
                            cancelButton: false,
                        })
                        .then((userChoice) => {
                            if (userChoice === confirmModule.USER_CHOICE_SAVE) {
                                let anchorId = removeCardButton.dataset.anchorId;
                                // we only want to apply the search on the editor it was invoked on
                                let parentEditor = utilModule.findParentWithSelector(self.el, "#editor-container");
                                anchorLinkManager.cleanupDeletedAnchorDestinations(parentEditor, anchorId);

                                const opts = {
                                    id: removeCardButton.dataset.objectId,
                                };
                                self.pushEvent("remove-card", opts);
                            }
                        });
                });
            }

            // we have to write to the copy store
            const copyMenu = document.body.querySelector("#copy-card-button");
            if (copyMenu) {
                copyMenu.addEventListener("click", () => {
                    console.log("Copied to clipboard");
                    navigator.clipboard.writeText(`??application/ldc-copy-view##${copyMenu.dataset.id}`).then(
                        () => {},
                        (reason) => {
                            console.error(new Error("Unable to set clipboard text for copying of view"));
                            console.error(reason);
                        },
                    );
                });
            }

            /**
             * We implemented the name-change logic in JS, since it was easier than to deal with
             * LiveView changes
             */
            const formChangeArgumentName = this.el.querySelector("#formChangeArgumentName");
            if (formChangeArgumentName) {
                formChangeArgumentName.addEventListener("click", (event) => {
                    event.preventDefault();

                    const newTitle = formChangeArgumentName.querySelector("input[name='local-name']");
                    const id = formChangeArgumentName.querySelector("input[name='id']").value;

                    // On link to cards for newly created Arguments
                    if ("isNew" in formChangeArgumentName.dataset) {
                        const opts = {
                            id: id,
                            "local-name": newTitle.value,
                        };
                        console.log("set-object-local-name", opts);
                        self.pushEvent("set-object-local-name", opts);
                    } else {
                        // do a normal local-name-change
                        self.pushEvent("set-object-local-name", {
                            id: id,
                            "local-name": newTitle.value,
                        });
                    }
                });
            }
        },

        destroyed() {
            // cleanup any left-over popup
            cardViewUtils.removePopup();

            console.info("destroyed view area");
        },
    },
    ToolbarHook: {
        mounted() {
            const self = this;
            const copyViewBtn = this.el.querySelector("#copy-view-btn");
            const addRealmBtn = this.el.querySelector(".add-realm-button-click");

            // we want to be able to simply paste into the froala editor using the default OS
            // pasting mechanism (i.e. CTRL+V), so we need to write to the clients clipbaord from
            // this hook and cannot rely on only the LiveView callback mechanism
            if (copyViewBtn) {
                copyViewBtn.addEventListener("click", (e) => {
                    // Note that it would be much nicer to use a custom mime type, however at the time
                    // of implementation this is not supported by default in Firefox. So we use this
                    // workaround of some text that is unlikely to be created by the user by accident
                    console.log(`Copy ${copyViewBtn.dataset.stateId}`);
                    navigator.clipboard.writeText(`??application/ldc-copy-view##${copyViewBtn.dataset.stateId}`).then(
                        () => {},
                        (reason) => {
                            console.error(new Error("Unable to set clipboard text for copying of view"));
                            console.error(reason);
                        },
                    );
                });
            }

            if (addRealmBtn != null) {
                addRealmBtn.addEventListener("click", (e) => {
                    let name = prompt("Perspective name: ");
                    if (name) {
                        self.pushEvent("add-realm", {
                            name: name,
                        });
                    }
                });
            }

            addRealmDragAndDrop(this);
        },
        updated() {
            addRealmDragAndDrop(this);
            checkForRealmUnlinkButton(this, this.el);
        },
    },
};

export const EDITOR_INFO = {
    name: NAME,
    tag: TAG_NAME,
    copyPasteMemento: copyPasteMemento,
};
